import {
  createSavedViewsController,
  createSavedViewsHooks,
  INVENTORY_SCOPE,
  mergeSavedColumns,
  type SavedViewsAdapter,
  type SavedViewsHooks,
} from '@/features/saved-views';
import {
  useInventoryColumnStore,
  defaultColumns,
  DEFAULT_PINNED_LEFT,
  DEFAULT_PINNED_RIGHT,
  type ColumnConfig,
} from '@/stores/inventory/inventory-column-store';
import {
  useInventoryFilterStore,
  defaultLocalFilters,
  defaultFilterDisplayOptions,
} from '@/stores/inventory/inventory-filter-store';
import {
  mergePersistedDisplayOptions,
  mergePersistedFilters,
} from '@/stores/inventory/inventory-filter-migration';
import { useInventoryDataStore } from '@/stores/inventory/inventory-data-store';
import type {
  InventorySavedViewPayload,
  InventorySavedViewColumn,
} from './types';

// INVARIANT: every field read by `capture()` MUST be persisted by its
// owning store's `partialize`. Otherwise F5 clears the field in-memory
// while the snapshot (rehydrated from the server payload) still holds the
// old value — and `computeIsDirty()` will return true for a reload the
// user never triggered. Currently covered: column-store (columns, pinned*,
// L2CP expanded), filter-store (localInventoryFilters, filterDisplayOptions),
// data-store (inventorySorting).
function capture(): InventorySavedViewPayload {
  const colState = useInventoryColumnStore.getState();
  const filterState = useInventoryFilterStore.getState();
  const dataState = useInventoryDataStore.getState();

  return {
    columns: colState.columns.map((c: ColumnConfig, i: number): InventorySavedViewColumn => ({
      id: c.id,
      visible: c.visible,
      order: i,
    })),
    pinnedLeft: [...colState.pinnedLeft],
    pinnedRight: [...colState.pinnedRight],
    lentToCounterpartiesGroupExpanded: colState.lentToCounterpartiesGroupExpanded,
    filters: { ...filterState.localInventoryFilters },
    filterDisplayOptions: { ...filterState.filterDisplayOptions },
    sorting: dataState.inventorySorting.map((s) => ({ ...s })),
  };
}

/**
 * Brings a payload's `filters` and `filterDisplayOptions` to the current schema
 * through the store's own rehydrate merges: fields added after the view was
 * saved get their default (e.g. `exchanges`, LR-518; a display option for
 * `settledQuantity`), filters since removed are dropped (a display option of a
 * removed filter is kept, as on rehydrate).
 * The raw `set` in apply() bypasses that merge, so downstream
 * `filters.<newField>.length` reads would TypeError on a legacy view. A payload
 * without `filters` passes through — the consumer decides between
 * reset-to-defaults and keep-as-is.
 */
export function withBackfilledFilters<
  T extends {
    filters?: Partial<InventorySavedViewPayload['filters']>;
    filterDisplayOptions?: InventorySavedViewPayload['filterDisplayOptions'];
  },
>(
  payload: T,
): T & {
  filters?: InventorySavedViewPayload['filters'];
  filterDisplayOptions?: InventorySavedViewPayload['filterDisplayOptions'];
} {
  return payload?.filters
    ? {
        ...payload,
        filters: mergePersistedFilters(payload.filters, defaultLocalFilters, defaultLocalFilters),
        filterDisplayOptions: mergePersistedDisplayOptions(
          payload.filterDisplayOptions,
          defaultFilterDisplayOptions,
          defaultFilterDisplayOptions,
        ),
      }
    : (payload as T & { filters?: undefined });
}

function apply(payload: InventorySavedViewPayload | Record<string, never>): void {
  const p = (payload ?? {}) as Partial<InventorySavedViewPayload>;
  const colStore = useInventoryColumnStore.getState();
  const filterStore = useInventoryFilterStore.getState();
  const dataStore = useInventoryDataStore.getState();

  const payloadColumns = p.columns ?? [];
  if (payloadColumns.length > 0) {
    colStore.setColumns(mergeSavedColumns(colStore.columns, payloadColumns, defaultColumns));
  } else {
    // Empty payload (system view with {}, or post-delete fallback): the merge
    // branch above would keep the previous view's visibility/order untouched
    // because there are no saved entries to override with. Reset to factory
    // defaults instead — otherwise switching back to the system view after
    // using a customised view leaves the customised columns in place.
    colStore.resetColumns();
  }
  colStore.setLentToCounterpartiesGroupExpanded(p.lentToCounterpartiesGroupExpanded ?? false);
  useInventoryColumnStore.setState({
    pinnedLeft: p.pinnedLeft ?? DEFAULT_PINNED_LEFT,
    pinnedRight: p.pinnedRight ?? DEFAULT_PINNED_RIGHT,
  });

  // If payload has no filters (e.g. system default view with `{}`), reset
  // to factory defaults rather than crash.
  if (p.filters) {
    const backfilled = withBackfilledFilters(p);
    filterStore.setLocalInventoryFilters(backfilled.filters!);
    useInventoryFilterStore.setState({
      filterDisplayOptions: backfilled.filterDisplayOptions!,
    });
  } else {
    filterStore.clearLocalInventoryFilters();
  }

  dataStore.setInventorySorting(p.sorting ?? []);
}

/**
 * What capture() returns right after apply(payload), computed without touching
 * the stores — the dirty-check snapshot for the F5 skip-apply branch of
 * use-inventory-view-bootstrap, where apply() doesn't run. Mirrors apply()
 * field by field; a lockstep test pins the two together.
 */
export function toSavedSnapshot(
  payload: InventorySavedViewPayload | Record<string, never>,
): InventorySavedViewPayload {
  const p = (payload ?? {}) as Partial<InventorySavedViewPayload>;
  const columns = p.columns?.length
    ? mergeSavedColumns(useInventoryColumnStore.getState().columns, p.columns, defaultColumns)
    : defaultColumns;
  const backfilled = p.filters ? withBackfilledFilters(p) : null;
  return {
    columns: columns.map((c, i) => ({ id: c.id, visible: c.visible, order: i })),
    pinnedLeft: p.pinnedLeft ?? DEFAULT_PINNED_LEFT,
    pinnedRight: p.pinnedRight ?? DEFAULT_PINNED_RIGHT,
    lentToCounterpartiesGroupExpanded: p.lentToCounterpartiesGroupExpanded ?? false,
    filters: backfilled?.filters ?? defaultLocalFilters,
    filterDisplayOptions: backfilled?.filterDisplayOptions ?? defaultFilterDisplayOptions,
    sorting: p.sorting ?? [],
  };
}

function subscribeToChanges(listener: () => void): () => void {
  // One zustand subscribe per store. Any relevant-field change triggers the
  // listener; the controller then recomputes dirty via capture()+isEqual.
  let prevColumns = useInventoryColumnStore.getState().columns;
  let prevPinnedLeft = useInventoryColumnStore.getState().pinnedLeft;
  let prevPinnedRight = useInventoryColumnStore.getState().pinnedRight;
  let prevExpanded = useInventoryColumnStore.getState().lentToCounterpartiesGroupExpanded;
  const unsubCol = useInventoryColumnStore.subscribe((state) => {
    if (
      state.columns !== prevColumns ||
      state.pinnedLeft !== prevPinnedLeft ||
      state.pinnedRight !== prevPinnedRight ||
      state.lentToCounterpartiesGroupExpanded !== prevExpanded
    ) {
      prevColumns = state.columns;
      prevPinnedLeft = state.pinnedLeft;
      prevPinnedRight = state.pinnedRight;
      prevExpanded = state.lentToCounterpartiesGroupExpanded;
      listener();
    }
  });

  let prevFilters = useInventoryFilterStore.getState().localInventoryFilters;
  let prevDisplayOpts = useInventoryFilterStore.getState().filterDisplayOptions;
  const unsubFilter = useInventoryFilterStore.subscribe((state) => {
    if (
      state.localInventoryFilters !== prevFilters ||
      state.filterDisplayOptions !== prevDisplayOpts
    ) {
      prevFilters = state.localInventoryFilters;
      prevDisplayOpts = state.filterDisplayOptions;
      listener();
    }
  });

  let prevSorting = useInventoryDataStore.getState().inventorySorting;
  const unsubData = useInventoryDataStore.subscribe((state) => {
    if (state.inventorySorting !== prevSorting) {
      prevSorting = state.inventorySorting;
      listener();
    }
  });

  return () => {
    unsubCol();
    unsubFilter();
    unsubData();
  };
}

export const inventoryAdapter: SavedViewsAdapter<InventorySavedViewPayload> = {
  scope: INVENTORY_SCOPE,
  capture,
  apply,
  subscribeToChanges,
};

export const inventorySavedViewsController = createSavedViewsController(inventoryAdapter);

const inventorySavedViewsHooks = createSavedViewsHooks<InventorySavedViewPayload>(INVENTORY_SCOPE);
export function getInventorySavedViewsHooks(): SavedViewsHooks<InventorySavedViewPayload> {
  return inventorySavedViewsHooks;
}
